import { RuleType } from 'Constants/Configuration'
import { TranslationKeys } from 'Constants/TranslationKeys'
import {
  obtainExcelDataFromDiscountData,
  obtainExcelDataFromsubRuleArticles,
  retrieveChannelMatchWithPricelist,
  retrieveSiteMatchWithPriceList,
  retrieveStoresMatchWithPricelist,
} from 'Helpers/DiscountHelper'
import { isDevelopment, isProdSite, renderTranslation, sendMessage } from 'Helpers/GenericHelpers'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import { Button, Popover, Upload, UploadProps, message } from 'antd'
import ExcelJS from 'exceljs'
import { useAppDispatch, useAppSelector } from 'hooks'
import { CloudUploadOutlined, ReadOutlined } from '@ant-design/icons'
import { importDiscountData } from 'Services/Omnichannel/DiscountService'
import { Spin } from 'antd'
import { useEffect, useMemo, useState } from 'react'

interface ButtonExcelProps {
  filename: string
  RowHead: RowHead
  RuleId: number
  subrule: boolean
  editDisabled: boolean
  typeOfChannel: number
  onTriggerClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}
interface RowHead {
  RuleName: string
  Season: string
  DateStart: string
  DecimalRound: string
  DateEnd: string
  RuleType: RuleType
}

const ExcelButton: React.FC<ButtonExcelProps> = ({
  filename: filename,
  RowHead: RowHead,
  RuleId: RuleId,
  subrule: subrule,
  editDisabled: editDisabled,
  typeOfChannel: typeOfChannel,
  onTriggerClick,
}) => {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const discountData = useAppSelector(state => state.present.discount.discountData)
  const listings = useAppSelector(state => state.present.configuration.allPricelists)
  const channelType: TypeOfChannel = useAppSelector(state => state.present.price.channelType)
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const pricelists = useAppSelector(state => state.present.site.pricelists)
  const stores = useAppSelector(state => state.present.store.stores)
  const subRuleArticles = useAppSelector(state => state.present.subRule.subRuleArticles)
  const [loading, setLoading] = useState(false)
  const columnlisting = subrule ? 20 : 6
  const columnfreeze = subrule ? 4 : 5
  const filterColumns = useAppSelector(state => state.present.subRule.filterColumns)

  const extraCol =  useMemo(() => {
     return JSON.parse(JSON.stringify(filterColumns))
  }, [filterColumns])


  const readFileAsync = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = event => {
        resolve(event.target.result)
      }
      reader.onerror = error => {
        reject(error)
      }
      reader.readAsArrayBuffer(file)
    })
  }

  const customRequest = async uploadObj => {
    const fileContent = await readFileAsync(uploadObj.file)
    const databefore = obtainExcelDataFromsubRuleArticles(subRuleArticles)
    dispatch(
      importDiscountData(
        RuleId,
        fileContent,
        databefore.length,
        Object.keys(databefore[0]).join(','),
        Object.values(databefore).map(x => ({ artid: x.articleid, artcode: x.articleCode })),
      ),
    )
      .then(() => {
        window.location.reload()
      })
      .catch(error => {
        message.error('Error importing discount data: ', error)
      })
  }
  const props: UploadProps = {
    beforeUpload: file => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      if (!isExcel) {
        message.error(`${file.name} is not a excel file`)
      }
      return isExcel || Upload.LIST_IGNORE
    },
    showUploadList: false,
    customRequest: customRequest,
  }

  const listToShowByType = (channelType, channelList, pricelists, stores, currentListing) => {
    switch (channelType) {
      case TypeOfChannel.Marketplace:
        const channelBaseOnPricelist = retrieveChannelMatchWithPricelist(currentListing, channelList)
        return channelBaseOnPricelist.map(x => x.name).join(', ')
      case TypeOfChannel.Site:
        const siteBasedOnPricelist = retrieveSiteMatchWithPriceList(currentListing, pricelists)
        return siteBasedOnPricelist.join(', ')
      case TypeOfChannel.Store:
        const storesBasedOnPricelist = retrieveStoresMatchWithPricelist(currentListing, stores)
        return storesBasedOnPricelist.map(x => x.name).join(', ')
      default:
        return ''
    }
  }
  const ruletypelabel = [
    { label: renderTranslation(language, TranslationKeys.ruleBoxInSeason), value: RuleType.season },
    {
      label: renderTranslation(language, TranslationKeys.ruleBoxSalesPeriod),
      value: RuleType.salesPeriod,
    },
    { label: renderTranslation(language, TranslationKeys.ruleBoxPromo), value: RuleType.promo },
  ]

  const listHeaderTitle = channelType => {
    switch (channelType) {
      case TypeOfChannel.Marketplace:
        return renderTranslation(language, TranslationKeys.listingPopListingTitle)
      case TypeOfChannel.Site:
        return renderTranslation(language, TranslationKeys.listingPopListingTitleSite)
      case TypeOfChannel.Store:
        return renderTranslation(language, TranslationKeys.listingPopListingTitleStore)
      default:
        return ''
    }
  }

  const Addheaders = worksheet => {
    for (let row = 1; row <= 2; row++) {
      const columnA = worksheet.getCell('A' + row)
      columnA.border = {
        bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
      }
      columnA.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9D9D9' },
      }
      columnA.font = { bold: true }

      const columnC = worksheet.getCell('C' + row)
      columnC.border = {
        right: { style: 'thin', color: { argb: 'FFFFFF' } },
        bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
      }

      columnC.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F2F2F2' },
      }

      if (row === 1) {
        columnA.value = {
          richText: [
            { text: renderTranslation(language, TranslationKeys.ruleBoxStartDate) + ': ', font: { bold: true } },
            { text: RowHead.DateStart },
          ],
        }
        if (RowHead.RuleType !== RuleType.season)
          columnC.value = {
            richText: [
              {
                text: renderTranslation(language, TranslationKeys.ruleBoxEndDate) + ': ',
                font: { bold: true },
              },
              { text: RowHead.DateEnd },
            ],
          }
      }
      if (row === 2) {
        columnA.value = {
          richText: [
            { text: renderTranslation(language, TranslationKeys.tableSeason) + ': ', font: { bold: true } },
            { text: RowHead.Season },
            {
              text: ' - ' + renderTranslation(language, TranslationKeys.listingPopListingType) + ': ',
              font: { bold: true },
            },
            {
              text: ruletypelabel
                .filter(x => x.value === RowHead.RuleType)
                .map(x => x.label)
                .toString(),
            },
          ],
        }
        columnC.value = {
          richText: [
            {
              text: renderTranslation(language, TranslationKeys.ruleBoxDecimalPricePlaceholder) + ': ',
              font: { bold: true },
            },
            { text: RowHead.DecimalRound },
          ],
        }
      }
    }
  }
  const GenerateExcelRule = async worksheet => {
    const acqcell = []
    const vencell = []
    await dispatch(obtainExcelDataFromDiscountData(discountData)).then(resp => {
      resp.RowWorkDetails.forEach(item1 => {
        const matchingItem = resp.statDetails.find(
          item2 =>
            item2.brand.name === item1.f1_brand &&
            item2.supergroup.name === item1.f2_supergroup &&
            item2.sector.name === item1.f3_sector,
        )

        if (matchingItem) {
          item1.f4_sellout = matchingItem.sellout
          item1.f5_markup = matchingItem.markup
        }
      })
      const rowDetailsHeadArray = [...Object.keys(resp.RowWorkDetails[0]).sort()]
      const excelrows = resp.RowWorkDetails.filter(obj => {
        return Object.keys(obj)
          .filter(key => key !== 'f1_brand' && key !== 'f3_sector' && key !== 'f2_supergroup')
          .every(key => obj[key] !== null)
      }).map(obj => Object.fromEntries([...Object.keys(obj).sort()].map(key => [key, obj[key]])))

      const countcolvisbile = Object.keys(rowDetailsHeadArray).length
      worksheet.views = [{ state: 'frozen', xSplit: columnfreeze, ySplit: 4, topLeftCell: 'F11' }]

      Addheaders(worksheet)

      worksheet.mergeCells('A3:D3')
      worksheet.mergeCells('A1:B1')
      worksheet.mergeCells('C1:D1')
      worksheet.mergeCells('A2:B2')
      worksheet.mergeCells('C2:D2')

      const RowRule = worksheet.getCell('A3')
      RowRule.value = RowHead.RuleName
      RowRule.alignment = { horizontal: 'center', vertical: 'middle' }
      RowRule.font = { bold: true, size: 12 }

      const rowtype = worksheet.getRow(1)
      const rowiva = worksheet.getRow(2)
      const rowchannel = worksheet.getRow(3)
      const row = worksheet.addRow(rowDetailsHeadArray.map(key => key.replace(/.*_/, '')))
      row.height = 30
      for (let column = 1; column <= countcolvisbile; column++) {
        const cell = row.getCell(column)
        cell.border = {
          top: { style: 'medium' },
          right: { style: 'medium' },
          bottom: { style: 'medium' },
          left: { style: 'medium' },
        }

        worksheet.getColumn(column).width = 12

        if (column > 4 && column < columnlisting - 1) worksheet.getColumn(column).width = 9
        if (column < 4) worksheet.getColumn(column).width = 20
        if (column === columnlisting - 1) {
          const cellabeltype = rowtype.getCell(column)
          cellabeltype.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          cellabeltype.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ebf1de' },
          }
          cellabeltype.value = renderTranslation(language, TranslationKeys.listingPopListingType) + ':'
          cellabeltype.font = { bold: true }
          const cellabeliva = rowiva.getCell(column)
          cellabeliva.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          cellabeliva.value = renderTranslation(language, TranslationKeys.listingPopListingVat) + ':'
          cellabeliva.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' },
          }
          cellabeliva.font = { bold: true }
          const cellabelchan = rowchannel.getCell(column)

          cellabelchan.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          cellabelchan.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'c4d79b' },
          }
          cellabelchan.value = listHeaderTitle(channelType) + ':'
          cellabelchan.font = { bold: true }
          cellabelchan.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
        }
        if (column < columnlisting) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' }
          if (column !== 1 && column !== 4 && column !== 5) cell.font = { color: { argb: 'FFFFFFFF' } }
          if (column === 4) cell.value = `${cell.value} (%)`
          if (column === 5) cell.value = `${cell.value} (%)`
        } else {
          const ivacell = rowiva.getCell(column)
          cell.alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' }
          const listingInformation = listings.find(x => x.name === cell.text)
          if (!listingInformation) continue
          const isListingTypeSell = (listingInformation && listingInformation.isSell) || !listingInformation
          ivacell.value = listingInformation.ltVat
            ? renderTranslation(language, TranslationKeys.listingPopListingVatYes)
            : renderTranslation(language, TranslationKeys.listingPopListingVatNo)
          const labelsell = isListingTypeSell
            ? renderTranslation(language, TranslationKeys.listingPopListingSell)
            : renderTranslation(language, TranslationKeys.listingPopListingBuy)
          ivacell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'd8e4bc' },
          }
          ivacell.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          ivacell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
          const typecell = rowtype.getCell(column)
          typecell.value =
            labelsell +
            ' ' +
            (listingInformation && listingInformation.b2c
              ? renderTranslation(language, TranslationKeys.listingPopListingB2C)
              : '')
          typecell.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          typecell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ebf1de' },
          }
          typecell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
          const chancell = rowchannel.getCell(column)
          chancell.value = listToShowByType(channelType, channelList, pricelists, stores, cell.text)
          chancell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'c4d79b' },
          }
          chancell.border = {
            top: { style: 'thin', color: { argb: 'FFFFFF' } },
            right: { style: 'thin', color: { argb: 'FFFFFF' } },
            bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
            left: { style: 'thin', color: { argb: 'FFFFFF' } },
          }
          chancell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }

          if (!isListingTypeSell) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'ffe8e8' },
            }
            acqcell.push(column)
          } else {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'f2f2f2' },
            }
            vencell.push(column)
          }
        }
      }

      excelrows
        .sort(
          (a, b) =>
            a.f1_brand.localeCompare(b.f1_brand) ||
            a.f2_supergroup.localeCompare(b.f2_supergroup) ||
            a.f3_sector.localeCompare(b.f3_sector),
        )
        .forEach(row => {
          let rowcell = worksheet.addRow(Object.keys(row).map(key => row[key]))
          for (let i = 1; i <= countcolvisbile; i++) {
            rowcell.getCell(i).border = {
              right: { style: 'thin', color: { argb: 'BFBFBF' } },
              bottom: { style: 'thin', color: { argb: 'BFBFBF' } },
              left: { style: 'thin', color: { argb: 'BFBFBF' } },
            }
            if (acqcell.includes(i)) {
              rowcell.getCell(i).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'ffe8e8' },
              }
            }
            if (vencell.includes(i)) {
              rowcell.getCell(i).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'f2f2f2' },
              }
            }
          }
        })

      worksheet.getCell('A4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DCC7A1' },
      }

      worksheet.getCell('B4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '062341' },
      }

      worksheet.getCell('C4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '6387a5' },
      }
      worksheet.getCell('D4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'CCC0DA' },
      }
      worksheet.getCell('E4').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'CCC0DA' },
      }
      return worksheet
    })
  }

  const GenerateExcelSubRule = async worksheet => {
    const acqcell = []
    const vencell = []
    const RowWorkDetails = obtainExcelDataFromsubRuleArticles(subRuleArticles)

    const rowDetailsHeadArray = Object.keys(RowWorkDetails[0]).map(key => key)
    const excelrows = RowWorkDetails.filter(obj => {
      return Object.keys(obj)
        .filter(key => key !== 'brand' && key !== 'settore' && key !== 'supergruppo')
        .every(key => obj[key] !== null)
    })
    const excelcolums = RowWorkDetails[0]
    const countcolvisbile = Object.keys(excelcolums).length - 1

    worksheet.views = [{ state: 'frozen', xSplit: columnfreeze, ySplit: 4, topLeftCell: 'F11' }]

    Addheaders(worksheet)

    worksheet.mergeCells('B1:D1')
    worksheet.mergeCells('B2:D2')
    worksheet.mergeCells('A3:D3')

    const RowRule = worksheet.getCell('A3')
    RowRule.value = RowHead.RuleName
    RowRule.alignment = { horizontal: 'center', vertical: 'middle' }
    RowRule.font = { bold: true, size: 12 }
    const rowtype = worksheet.getRow(1)
    const rowiva = worksheet.getRow(2)
    const rowchannel = worksheet.getRow(3)
    const row = worksheet.addRow(
      rowDetailsHeadArray.filter(key => key !== 'articleid').map(key => key.replace(/_.*/, '')),
    )
    row.height = 30

    for (let column = 1; column <= countcolvisbile; column++) {
      const cell = row.getCell(column)
      cell.border = {
        top: { style: 'medium' },
        right: { style: 'medium' },
        bottom: { style: 'medium' },
        left: { style: 'medium' },
      }

      if (column < 2) {
        worksheet.getColumn(column).width = 30
      } else {
        worksheet.getColumn(column).width = 13
      }
      if (column > 4 && column !== 9 && column !== 13 && column < columnlisting - 2)
        worksheet.getColumn(column).width = 9

      if (column === columnlisting - 1) {
        const cellabeltype = rowtype.getCell(column)
        cellabeltype.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        cellabeltype.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ebf1de' },
        }
        cellabeltype.value = renderTranslation(language, TranslationKeys.listingPopListingType) + ':'
        cellabeltype.font = { bold: true }
        const cellabeliva = rowiva.getCell(column)
        cellabeliva.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        cellabeliva.value = renderTranslation(language, TranslationKeys.listingPopListingVat) + ':'
        cellabeliva.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd8e4bc' },
        }
        cellabeliva.font = { bold: true }
        const cellabelchan = rowchannel.getCell(column)
        cellabelchan.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        cellabelchan.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'c4d79b' },
        }
        cellabelchan.value = listHeaderTitle(channelType) + ':'
        cellabelchan.font = { bold: true }
        cellabelchan.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
      }
      if (column < columnlisting) {
        cell.alignment = { horizontal: 'left', vertical: 'middle' }
        if (column === 9 || column === 13) cell.value = `${cell.value} (%)`
        if (column === columnlisting - 1 || column === columnlisting - 2) cell.value = `${cell.value} (€)`
      } else {
        const ivacell = rowiva.getCell(column)
        cell.alignment = { wrapText: true, horizontal: 'center', vertical: 'middle' }
        const listingInformation = listings.find(x => x.name === cell.text)
        if (!listingInformation) continue
        const isListingTypeSell = listingInformation && listingInformation.isSell
        ivacell.value = listingInformation.ltVat
          ? renderTranslation(language, TranslationKeys.listingPopListingVatYes)
          : renderTranslation(language, TranslationKeys.listingPopListingVatNo)
        const labelsell = isListingTypeSell
          ? renderTranslation(language, TranslationKeys.listingPopListingSell)
          : renderTranslation(language, TranslationKeys.listingPopListingBuy)
        ivacell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'd8e4bc' },
        }
        ivacell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        ivacell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
        const typecell = rowtype.getCell(column)
        typecell.value =
          labelsell +
          ' ' +
          (listingInformation && listingInformation.b2c
            ? renderTranslation(language, TranslationKeys.listingPopListingB2C)
            : '')
        typecell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        typecell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'ebf1de' },
        }
        typecell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }
        const chancell = rowchannel.getCell(column)
        chancell.value = listToShowByType(channelType, channelList, pricelists, stores, cell.text)
        chancell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'c4d79b' },
        }
        chancell.border = {
          top: { style: 'thin', color: { argb: 'FFFFFF' } },
          right: { style: 'thin', color: { argb: 'FFFFFF' } },
          bottom: { style: 'thin', color: { argb: 'FFFFFF' } },
          left: { style: 'thin', color: { argb: 'FFFFFF' } },
        }
        chancell.alignment = { wrapText: true, horizontal: 'left', vertical: 'top' }

        if (!isListingTypeSell) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'ffe8e8' },
          }
          acqcell.push(column)
        } else {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f2f2f2' },
          }
          vencell.push(column)
        }
      }
    }

    excelrows
      .sort((a, b) => a.articleCode.localeCompare(b.articleCode))
      .forEach(row => {
        let rowcell = worksheet.addRow(
          Object.keys(row)
            .filter(key => key !== 'articleid')
            .map(key => row[key]),
        )

        for (let i = 1; i <= countcolvisbile; i++) {
          if (i === 9 || i === 13) rowcell.getCell(i).font = { italic: true }
          rowcell.getCell(i).border = {
            right: { style: 'thin', color: { argb: 'BFBFBF' } },
            bottom: { style: 'thin', color: { argb: 'BFBFBF' } },
            left: { style: 'thin', color: { argb: 'BFBFBF' } },
          }
          if (acqcell.includes(i)) {
            rowcell.getCell(i).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'ffe8e8' },
            }
          }
          if (vencell.includes(i)) {
            rowcell.getCell(i).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'f2f2f2' },
            }
          }
        }
      })

    for (let row = 1; row <= 4; row++) {
      for (let col = columnlisting; col <= countcolvisbile; col += 5) {
        worksheet.mergeCells(row, col, row, col + 4)
        if (row === 4) {
          const text = worksheet.getRow(4).getCell(col).text
          worksheet.getRow(row).getCell(col).value = {
            richText: [
              { text: text },
              { text: '\nvenqta            ' },
              { text: '   venval (€)       ' },
              { text: '                sc (%)   ' },
              { text: '           mark (%)' },
              { text: '         markavg (%)' },
            ],
          }
        }
      }
    }

    worksheet.getRow(4).getCell(5).alignment = { wrapText: true, horizontal: 'center', vertical: 'top' }
    worksheet.getRow(4).getCell(5).value = {
      richText: [{ text: 'ordinato' }, { text: '\n qtà                  ' }, { text: '    val (€)' }],
    }
    worksheet.getRow(4).getCell(7).alignment = { wrapText: true, horizontal: 'center', vertical: 'top' }
    worksheet.getRow(4).getCell(7).value = {
      richText: [{ text: 'consegnato' }, { text: '\n qtà                  ' }, { text: '    val (€)' }],
    }
    worksheet.getRow(4).getCell(10).alignment = { wrapText: true, horizontal: 'center', vertical: 'top' }
    worksheet.getRow(4).getCell(10).value = {
      richText: [{ text: 'venduto' }, { text: '\n qtà                  ' }, { text: '    val (€)' }],
    }
    worksheet.getRow(4).getCell(14).alignment = { wrapText: true, horizontal: 'center', vertical: 'top' }
    worksheet.getRow(4).getCell(14).value = {
      richText: [{ text: 'esistenza' }, { text: '\n qtà                  ' }, { text: '    val (€)' }],
    }
    worksheet.getRow(4).getCell(16).alignment = { wrapText: true, horizontal: 'center', vertical: 'top' }
    worksheet.getRow(4).getCell(16).value = {
      richText: [{ text: 'disponiblità' }, { text: '\n qtà                  ' }, { text: '    val (€)' }],
    }

    worksheet.mergeCells('E4:F4')
    worksheet.mergeCells('G4:H4')
    worksheet.mergeCells('J4:K4')
    worksheet.mergeCells('N4:O4')
    worksheet.mergeCells('P4:Q4')

    for (let col = 1; col < columnlisting; col += 1) {
      if (col === 1) {
        worksheet.getRow(4).getCell(col).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'DCC7A1' },
        }
      }
      if (col > 1 && col < 5) {
        worksheet.getRow(4).getCell(col).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'C5D2DD' },
        }
      }
      if (col >= 5) {
        worksheet.getRow(4).getCell(col).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'AEEF9F' },
        }
      }
    }
  }

  const fileName = (filename + '_' + new Date().toLocaleString())
    .replaceAll('/', '-')
    .replaceAll(',', '-')
    .replaceAll(':', '-')

  const exportToExcel = async () => {
    
    setLoading(true)

    sendMessage({ type: 'Excel', operationType: 0, ruleId: RuleId, isSubRule: subrule, typeOfChannel: typeOfChannel ,extraColumn: extraCol})
    setTimeout(() => setLoading(false), 1000)

    //commento il download locale. viene fatto da Atelier9
    // setLoading(true)
    // const workbook = new ExcelJS.Workbook()
    // const worksheet = workbook.addWorksheet(fileName)

    // if (subrule) {
    //   await GenerateExcelSubRule(worksheet)
    // } else {
    //   await GenerateExcelRule(worksheet)
    // }

    // const buffer = await workbook.xlsx.writeBuffer()
    // const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    // const url = window.URL.createObjectURL(blob)
    // const link = document.createElement('a')
    // link.href = url
    // link.setAttribute('download', fileName + '.xlsx')
    // document.body.appendChild(link)
    // link.click()
    // document.body.removeChild(link)
    // setLoading(false)
    // message.info('Excel Downloaded.')
  }

  const content = () => (
    <Spin spinning={loading} size='large' style={{ maxHeight: '100%' }}>
      <div>
        <div>
          <Button style={{ width: 100 }} onClick={exportToExcel}>
            <ReadOutlined />
            Export
          </Button>
        </div>
        {subrule && !editDisabled ? (
          <div style={{ marginTop: '5px' }}>
            {/* <Upload {...props}> */}
            <Button
              style={{ width: 100, marginBottom: '5px' }}
              onClick={() =>
                sendMessage({
                  type: 'Excel',
                  operationType: 1,
                  ruleId: RuleId,
                  isSubRule: subrule,
                  typeOfChannel: typeOfChannel,
                })
              }
            >
              <CloudUploadOutlined />
              Import
            </Button>
            {/* </Upload> */}
          </div>
        ) : null}
      </div>
    </Spin>
  )

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (onTriggerClick) onTriggerClick(e)
  }

  return (
    <Popover placement='bottomLeft' content={content()} trigger='click'>
      {/* <Button onClick={handleClick}> */}
      <Button>
        Excel
        <svg xmlns='http://www.w3.org/2000/svg' x='0px' y='0px' width='16' height='16' viewBox='0 0 40 30'>
          <path fill='#4CAF50' d='M41,10H25v28h16c0.553,0,1-0.447,1-1V11C42,10.447,41.553,10,41,10z'></path>
          <path
            fill='#FFF'
            d='M32 15H39V18H32zM32 25H39V28H32zM32 30H39V33H32zM32 20H39V23H32zM25 15H30V18H25zM25 25H30V28H25zM25 30H30V33H25zM25 20H30V23H25z'
          ></path>
          <path fill='#2E7D32' d='M27 42L6 38 6 10 27 6z'></path>
          <path
            fill='#FFF'
            d='M19.129,31l-2.411-4.561c-0.092-0.171-0.186-0.483-0.284-0.938h-0.037c-0.046,0.215-0.154,0.541-0.324,0.979L13.652,31H9.895l4.462-7.001L10.274,17h3.837l2.001,4.196c0.156,0.331,0.296,0.725,0.42,1.179h0.04c0.078-0.271,0.224-0.68,0.439-1.22L19.237,17h3.515l-4.199,6.939l4.316,7.059h-3.74V31z'
          ></path>
        </svg>
      </Button>
    </Popover>
  )
}

export default ExcelButton
